<template>
  <DatatableReportV3
    v-if="!gateLoading"
    id="invoices-table"
    ref="invoices_datatable"
    :key="progressBillingEnabled ? 'spb' : 'legacy'"
    title="Invoices"
    class="no-cursor"
    :config="config"
    :source="source"
    :filters="filtersWithFinalizedToggle"
    :map-records="applyFilter"
    subpage
    :flat="flat"
    no-download
    :min-height="400"
    :bulk-select="multiSelect"
    :bulk-select-keep-alive="multiSelect"
    :bulk-select-across-pages="multiSelect"
    bulk-select-key="checkbox_row_value"
    :bulk-select-ids="bulkSelectIds"
    @update:bulk-select-ids="$emit('update:bulk-select-ids', $event)"
  >
    <template #column-invoice="row">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <span @click="viewInvoice(row, $event)" v-html="row.columnValue"></span>
    </template>
    <template #actions>
      <slot name="actions"></slot>
    </template>
  </DatatableReportV3>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import Badge from "@/components/Badge.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";

export default {
  components: {
    DatatableReportV3
  },
  props: {
    flat: {
      type: Boolean,
      default: false
    },
    address: {
      type: Object,
      default: null
    },
    filters: {
      type: Array,
      default: () => []
    },
    source: {
      type: String,
      default: ""
    },
    customerId: {
      type: Number,
      default: null
    },
    showSubCustomers: {
      type: Boolean,
      default: false
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    bulkSelectIds: {
      type: Array,
      default: () => []
    }
  },
  emits: ["view-invoice", "update:bulk-select-ids"],
  data() {
    return {
      progressBillingConfig: null,
      gateLoading: true
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant"]),
    progressBillingEnabled() {
      return this.featureEnabledForTenant("progress_billing");
    },
    filtersWithFinalizedToggle() {
      // Finalized / All toggle. Default state is OFF → label-off label is
      // "Finalized" → no `include_drafts` query param is sent → the
      // RevenueReport scope keeps its `.finalized` constraint. Toggling
      // ON sends `include_drafts=true` and the constraint is dropped so
      // draft / pending invoices show up too.
      return [
        ...this.filters,
        {
          type: "toggle",
          name: "include_drafts",
          options: ["Finalized", "All"]
        }
      ];
    },
    config() {
      return {
        // the checkbox column is unshifted to index 0 when multi-select is on
        order: [[this.multiSelect ? 1 : 0, "desc"]],
        columns: this.progressBillingEnabled
          ? this.spbColumns
          : this.legacyColumns,
        vueComponents: [
          {
            rootId: "badge-vue",
            component: Badge
          }
        ]
      };
    },
    legacyColumns() {
      return [
        { data: "completed_on", title: "Date" },
        { data: "invoice" },
        {
          data: "last_sent_badge",
          title: "Sent",
          orderable: false,
          searchable: false,
          className: "invoice-sent-badge"
        },
        { data: "job" },
        {
          data: "customer",
          title: "Customer",
          orderable: false,
          visible: this.showSubCustomers,
          filterName: "customer_id"
        },
        {
          data: "location_name",
          title: "Location Name",
          orderable: false,
          visible: !this.address
        },
        {
          data: "service_address",
          title: "Service Address",
          orderable: false,
          visible: !this.address
        },
        {
          data: "total",
          title: "Invoice Total",
          className: "text-right",
          searchable: false
        },
        {
          data: "balance",
          title: "Balance Due",
          className: "text-right",
          searchable: false,
          createdCell: function (td, cellData) {
            if (cellData !== "--") {
              $(td).addClass("text-danger");
            }
          }
        },
        { data: "batch_date", title: "Batch Date", searchable: false }
      ];
    },
    spbColumns() {
      return [
        { data: "completed_on", title: "Date" },
        { data: "invoice" },
        {
          data: "last_sent_badge",
          title: "Sent",
          orderable: false,
          searchable: false,
          className: "invoice-sent-badge"
        },
        { data: "job" },
        {
          data: "customer",
          title: "Customer",
          orderable: false,
          visible: this.showSubCustomers,
          filterName: "customer_id"
        },
        {
          data: "location_name",
          title: "Location Name",
          orderable: false,
          visible: !this.address
        },
        {
          data: "service_address",
          title: "Service Address",
          orderable: false,
          visible: !this.address
        },
        {
          data: "total",
          title: "Invoice Total",
          className: "text-right",
          searchable: false
        },
        {
          data: "paid_to_date",
          title: "Paid to Date",
          className: "text-right",
          searchable: false,
          orderable: false
        },
        {
          data: "balance",
          title: "Balance Due",
          className: "text-right",
          searchable: false,
          createdCell: function (td, cellData) {
            if (cellData !== "--") {
              $(td).addClass("text-danger");
            }
          }
        },
        {
          data: "payment_status",
          title: "Payment Status",
          orderable: false,
          searchable: false
        },
        { data: "batch_date", title: "Batch Date", searchable: false }
      ];
    }
  },
  async created() {
    if (!this.featureEnabledForTenant("progress_billing")) {
      this.gateLoading = false;
      return;
    }
    try {
      this.progressBillingConfig = await this.fetchProgressBillingConfig();
    } catch {
      this.progressBillingConfig = null;
    } finally {
      this.gateLoading = false;
    }
  },
  methods: {
    ...mapActions(["fetchProgressBillingConfig"]),
    applyFilter(record) {
      record.customer_id = this.customerId;
      return record;
    },
    viewInvoice(row, e) {
      e.preventDefault();
      this.$emit("view-invoice", row.DT_RowId);
    },
    refresh() {
      this.$refs.invoices_datatable?.refreshTable();
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.invoice-sent-badge) {
  max-width: 100px;
  .sera-badge {
    margin-left: 0px;
    justify-content: center !important;
    max-width: 75px;
  }
}

:deep(td) {
  vertical-align: top !important;
}
</style>
